import React, { useCallback, useState } from 'react'
import style from './contribute.module.css'
import mainstyle from '../../index.module.css'
import Page from '../../components/layout/page.component'
import FileUploader, { FileUploaderProps } from '../../components/file-uploader/file-uploader.component'
import { Button, TextField } from '@mui/material'
import ConfirmationDialog from '../../components/dialogs/confirmation.dialog'
import { toast } from 'react-toastify'
import { useLoading } from '../../providers/lolading.provider'
import { ContributeItemRequest, useContributeService } from './contribute.service'
import { useSearchParams } from 'react-router-dom'

interface Contribution {
    items: ContributionItem[],
}

interface ContributionItem {
    id: string,
    content: string | undefined,
    text: string,
    size?: number
}

const imagePrefix = `partitura_img_`;
const maxSize = 5;

export default function Contribute() {
    const [current, setCurrent] = useState<Contribution>({ items: [{ id: `${imagePrefix}0`, content: undefined, text: '' }] });
    const [dialogConfirmVisible, setDialogConfirmVisible] = useState(false);
    const [sendingInfo, setSendingInfo] = useState(false);
    const [infoSent, setInfoSent] = useState(false);

    const [searchParams] = useSearchParams();
    const userName = searchParams.get("user");

    const service = useContributeService();

    const { setIsLoading, isLoading } = useLoading();

    const addImage = (id: string, data: any, text: string, size: number): boolean => {
        if (current.items.findIndex(f => f.content === data) !== -1) {
            toast.warn('Partitura já adicionada na lista atual');
            return false;
        }

        const totalSize = current.items.map(i => i.size ? i.size : 0).reduce((prev, curr) => prev += curr, 0) + size;

        if (totalSize > maxSize) {
            toast.warn(`O total de arquivos ultrapassou o limite de ${maxSize} Mb. Envie menos arquivos.`);
            return false;
        }

        const toModifyIndex = current.items.findIndex(f => f.id === id);

        current.items[toModifyIndex].content = data;
        current.items[toModifyIndex].size = size;
        const split = text.split('.');
        current.items[toModifyIndex].text = split.slice(0, split.length - 1).join('.');

        const newImages = current.items[current.items.length - 1].content !== undefined
            ? current.items.concat({ id: `${imagePrefix}${current.items.length}`, content: undefined, size: undefined, text: '' })
            : [...current.items];

        setCurrent({ items: newImages });

        return true;
    }

    const cleanImage = async (id: string) => {
        const toRemoveIndex = current.items.findIndex(f => f.id === id);

        const newImages = [...current.items];
        newImages.splice(toRemoveIndex, 1);
        newImages.forEach((item, index) => item.id = `${imagePrefix}${index}`)

        setCurrent(c => ({ ...c, items: newImages }));
    }

    function handleDrag(props: FileUploaderProps): void {
        console.log(props)
    }

    function handleDrop(props: FileUploaderProps): void {
        console.log(props)
    }

    const onConfirm = async () => {
        const localCurrent = { ...current };

        setSendingInfo(true);
        setIsLoading(true);

        try {
            let items: ContributeItemRequest[] = [];

            validItems(localCurrent.items)
                .forEach(image => items = items.concat({
                    content: image.content!,
                    name: image.text!
                }));

            await service.addImages({
                items,
                user: userName
            });

            setInfoSent(true);
        } catch {
            toast.error('Não foi possivel concluir sua contribuição. Tente novamente em alguns segundos.');
            setSendingInfo(false);
        }
        finally {
            setIsLoading(false);
        }
    }

    const validItems = useCallback((items?: ContributionItem[]) => (items ? items : current.items).filter(i => i.content !== undefined), [current.items]);

    const validate = useCallback(async () => {
        if (sendingInfo) return;

        if (!current.items ||
            validItems().length <= 0 ||
            validItems().some(c => !c.text)) {
            toast.error('Há campos obrigatórios que precisam ser preenchidos!');
            return;
        }

        setDialogConfirmVisible(true)
    }, [sendingInfo, current.items, validItems]);

    return <>
        <Page
            width='narrow'
            eyebrow='Contribuir'
            title={!infoSent ? 'Contribua com partituras e ganhe acesso VIP' : 'Obrigado por sua contribuição!'}
            subtitle={!infoSent ? `Envie imagens ou PDFs (até ${maxSize} Mb no total) e informe o título de cada partitura.` : undefined}>
            {!infoSent ? <div className={style.card}>
                {current.items.map((item, index) => <div
                    key={`div_partitura_container_${index}`}
                    className={style.item}>
                    <div className={mainstyle.lines} key={`div_partitura_img_${index}`}>
                        <FileUploader
                            id={`partitura_img_${index}`}
                            key={`partitura_img_${index}`}
                            onChange={addImage}
                            onClickImage={cleanImage}
                            photo={item.content}
                            handleDrag={handleDrag}
                            handleDrop={handleDrop}
                            clearInternalWhenClick={false}
                            disabled={isLoading}
                        />
                    </div>
                    {!!item.size && <div className={mainstyle.lines} key={`div_titulo-partitura-${index}`}>
                        <TextField
                            key={`titulo-partitura-${index}`}
                            InputLabelProps={{ shrink: true }}
                            className={mainstyle.txtboxfull}
                            id={`titulo-partitura-${index}`}
                            label="Titulo"
                            variant="outlined"
                            placeholder='Titulo da partitura (adicione nome do artista, album e ano se quiser).'
                            value={item.text}
                            onChange={(e) => {
                                const currItem = current.items[index];
                                currItem.text = e.target.value;

                                const newItems = [...current.items];

                                newItems.splice(index, 1, currItem);

                                setCurrent({ items: newItems });
                            }}
                            error={!!item.size && !item.text}
                            helperText={!!item.size && !item.text ? 'Campo obrigatório' : ''}
                            disabled={isLoading}
                        />
                    </div>}
                </div>)}

                <div className={mainstyle.lines}>
                    <Button onClick={validate} variant="contained" color="primary" disabled={isLoading}>
                        Enviar
                    </Button>
                </div>
            </div>
                : <>
                    <div className={mainstyle.lines}>
                        <Button onClick={() => window.location.reload()} variant="contained" color="primary" disabled={isLoading}>
                            Enviar mais contribuições
                        </Button>
                    </div>
                </>}
        </Page>

        {dialogConfirmVisible && <ConfirmationDialog
            title={`Confirmar o envio das '${validItems().length}' imagem(ns) ?`}
            onConfirm={onConfirm}
            text={'Clique em confirmar para iniciar o envio'}
            onClose={async () => setDialogConfirmVisible(false)}
        />}
    </>
}